import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const BASE_URL = 'https://store.mvtyres.com/backend';

const Account = () => {
  const navigate = useNavigate();

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleChange = (e, setter) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const getCsrfToken = async () => {
    const res = await fetch(`${BASE_URL}/csrf-token`, {
      credentials: 'include',
    });
    const data = await res.json();
    return data.csrfToken;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const { name, email, password, confirmPassword } = registerData;

    if (!name || !email || !password || !confirmPassword) {
      toast.error('All fields are required');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Invalid email format');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      const csrfToken = await getCsrfToken();

      const res = await fetch(`${BASE_URL}/user/register-submit`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({ firstName: name, lastName: '', email, password, retypePassword: confirmPassword }),
      });

      const result = await res.json();
      if (result.status) {
        toast.success('Registration successful!');
        localStorage.setItem('user', JSON.stringify(result.data));
        window.location.href = `${BASE_URL}/user/dashboard`;
      } else {
        toast.error(result.message || 'Registration failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred during registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const { username, password } = loginData;

    if (!username || !password) {
      toast.error('Please fill in all login fields.');
      return;
    }

    setIsLoggingIn(true);
    try {
      const csrfToken = await getCsrfToken();

      const res = await fetch(`${BASE_URL}/user/login-submit`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({ username, password }),
      });

      const result = await res.json();
      if (result.status) {
        toast.success('Login successful!');
        localStorage.setItem('user', JSON.stringify(result.data));
        window.location.href = `${BASE_URL}/user/dashboard`;
      } else {
        toast.error(result.message || 'Login failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while logging in');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const togglePassword = useCallback((fieldId) => {
    const passwordField = document.getElementById(fieldId);
    const toggleIcon = passwordField.nextElementSibling.querySelector('i');

    if (passwordField.type === "password") {
      passwordField.type = "text";
      toggleIcon.classList.remove("fa-eye");
      toggleIcon.classList.add("fa-eye-slash");
    } else {
      passwordField.type = "password";
      toggleIcon.classList.remove("fa-eye-slash");
      toggleIcon.classList.add("fa-eye");
    }
  }, []);

  return (
    <>
      <section className="inner-banner">
        <div className="container-fluid web-padding">
          <div className="row">
            <div className="col-md-6" data-aos="flip-left" data-aos-duration={2000}>
              <div className="inner-banner-left">
                <h1>Account</h1>
              </div>
            </div>
            <div className="col-md-6">
              <div className="inner-banner-right">
                <img src="images/hero-tires.png" alt="img" />
              </div>
            </div>
          </div>
        </div>
        <h2>Tyres</h2>
      </section>

      <section>
        <div className="login-sec">
          <div className="container">
            <div className="row">

              {/* Signup Form */}
              <div className="col-lg-6 col-md-6 col-12">
                <div className="login-box signup wow fadeInRight" data-wow-duration="2s">
                  <form onSubmit={handleRegisterSubmit}>
                    <h2>Sign Up</h2>
                    <div className="row">
                      <div className="col-lg-12">
                        <input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          value={registerData.name}
                          onChange={(e) => handleChange(e, setRegisterData)}
                        />
                      </div>
                      <div className="col-lg-12">
                        <input
                          type="email"
                          name="email"
                          placeholder="Your Email"
                          value={registerData.email}
                          onChange={(e) => handleChange(e, setRegisterData)}
                        />
                      </div>
                      <div className="col-lg-12">
                        <div className="password-wrapper">
                          <input
                            type="password"
                            name="password"
                            id="password"
                            placeholder="Password"
                            value={registerData.password}
                            onChange={(e) => handleChange(e, setRegisterData)}
                          />
                          <span className="toggle-password" onClick={() => togglePassword("password")}>
                            <i className="fa fa-eye" />
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="password-wrapper">
                          <input
                            type="password"
                            name="confirmPassword"
                            id="confirmPassword"
                            placeholder="Confirm Password"
                            value={registerData.confirmPassword}
                            onChange={(e) => handleChange(e, setRegisterData)}
                          />
                          <span className="toggle-password" onClick={() => togglePassword("confirmPassword")}>
                            <i className="fa fa-eye" />
                          </span>
                        </div>
                      </div>
                    </div>
                    <button type="submit" className="theme-btn-1" disabled={isSubmitting}>
                      {isSubmitting ? 'Signing up...' : 'Sign up'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Login Form */}
              <div className="col-lg-6 col-md-6 col-12">
                <div className="login-box wow fadeInLeft" data-wow-duration="2s">
                  <form onSubmit={handleLoginSubmit}>
                    <h2>Log in</h2>
                    <input
                      type="text"
                      name="username"
                      placeholder="User Name"
                      value={loginData.username}
                      onChange={(e) => handleChange(e, setLoginData)}
                    />
                    <input
                      type="password"
                      name="password"
                      placeholder="Password"
                      value={loginData.password}
                      onChange={(e) => handleChange(e, setLoginData)}
                    />
                    <ul>
                      <li>
                        <label>
                          <input type="checkbox" /> Remind Me
                        </label>
                      </li>
                    </ul>
                    <div className="login-btnRow">
                      <div>
                        <button type="submit" className="theme-btn" disabled={isLoggingIn}>
                          {isLoggingIn ? 'Logging in...' : 'Submit now'}
                          <span>
                            <img src="images/arrow-right.png" alt="icon" />
                          </span>
                        </button>
                      </div>
                      <div>
                        <Link to="/forgot-password">Forgot Password?</Link>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Account;
